import type { Column } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';

import type { IconName } from '~/chords';
import { Icon, Tooltip } from '~/components';
import { cn } from '~/lib/cn';

interface SortableIndicatorProps<T> {
  column: Column<T>;
  tooltip?: string;
  className?: string;
}

export function SortableIndicator<T>({
  column,
  tooltip,
  className,
}: SortableIndicatorProps<T>) {
  const { t } = useTranslation('components', {
    keyPrefix: 'sortableIndicator',
  });
  const sortDirection = column.getIsSorted();

  const isRestrictedColumn = ['streamsRtd', 'streamsTw'].includes(column.id);

  const isHandlerDisabled = isRestrictedColumn && sortDirection === 'desc';
  const clickHandler = isHandlerDisabled
    ? undefined
    : column.getToggleSortingHandler();
  const cursorClass = isHandlerDisabled
    ? 'cursor-not-allowed'
    : 'cursor-pointer';

  let iconType: IconName = 'arrow-up-arrow-down';

  if (sortDirection) {
    iconType = sortDirection === 'asc' ? 'arrow-up' : 'arrow-down';
  }

  const iconVariant = sortDirection ? 'solid' : 'light';

  return tooltip ? (
    <Tooltip
      content={<span className="font-semibold">{tooltip}</span>}
      delayDuration={0}
      sideOffset={16}
      side="top"
      align="end"
      size="md"
    >
      <span className={cn('w-md', className)}>
        <Icon
          type={iconType}
          variant={iconVariant}
          aria-label={t('iconLabel', {
            columnId: column.id,
          })}
          size="sm"
          className={cn(cursorClass, 'p-xs')}
          onClick={clickHandler}
        />
      </span>
    </Tooltip>
  ) : (
    <span className={cn('flex w-md items-center justify-center', className)}>
      <Icon
        type={iconType}
        variant={iconVariant}
        aria-label={t('iconLabel', {
          columnId: column.id,
        })}
        size="sm"
        className={cn(cursorClass, 'p-xs', {
          'text-default': sortDirection,
        })}
        onClick={clickHandler}
      />
    </span>
  );
}

SortableIndicator.displayName = 'SortableIndicator';
